import { Box, Typography } from '@mui/material';

interface TopbarProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Optional node rendered to the left of the title (e.g. a logo mark). */
  leading?: React.ReactNode;
  /** Keep title and actions on one row even on mobile instead of stacking. Default true (existing behaviour). */
  stackOnMobile?: boolean;
  /** Hide `leading` below sm. Default false (existing behaviour). */
  hideLeadingOnMobile?: boolean;
  /** Hide `subtitle` below sm. Default false (existing behaviour). */
  hideSubtitleOnMobile?: boolean;
}

export const Topbar = ({
  title,
  subtitle,
  actions,
  leading,
  stackOnMobile = true,
  hideLeadingOnMobile = false,
  hideSubtitleOnMobile = false,
}: TopbarProps) => {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        flexDirection: { xs: stackOnMobile ? 'column' : 'row', sm: 'row' },
        alignItems: { xs: stackOnMobile ? 'flex-start' : 'center', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1.5, sm: 2 },
        mb: { xs: 0, sm: 3 },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.25, sm: 1.75 },
        }}
      >
        {leading && (
          <Box
            sx={{
              display: { xs: hideLeadingOnMobile ? 'none' : 'flex', sm: 'flex' },
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {leading}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '1.5rem' },
              lineHeight: 1.2,
              pl: { xs: 1, sm: 0 },
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                display: { xs: hideSubtitleOnMobile ? 'none' : 'block', sm: 'block' },
                mt: { xs: 0.25, sm: 0.5 },
                color: 'text.secondary',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {actions && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1 },
            flexWrap: 'wrap',
            width: { xs: stackOnMobile ? '100%' : 'auto', sm: 'auto' },
            justifyContent: { xs: stackOnMobile ? 'flex-start' : 'flex-end', sm: 'flex-end' },
            flexShrink: 0,
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default Topbar;
